import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Server, Key, Lock, User } from 'lucide-react'
import { getMCPConfigSchema, type MCPFormData, type MCPAuthType } from '@/schemas/tool-config'

interface MCPConfigFormProps {
  defaultValues?: Partial<MCPFormData>
  onValuesChange?: (values: MCPFormData, isValid: boolean) => void
}

export function MCPConfigForm({ defaultValues, onValuesChange }: MCPConfigFormProps) {
  const { t } = useTranslation('tools')

  const form = useForm<MCPFormData>({
    resolver: zodResolver(getMCPConfigSchema()),
    defaultValues: {
      server_url: defaultValues?.server_url || '',
      auth_type: defaultValues?.auth_type || 'none',
      api_key: defaultValues?.api_key || '',
      username: defaultValues?.username || '',
      password: defaultValues?.password || '',
    },
    mode: 'onChange',
  })

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors, isValid },
  } = form

  const authType = watch('auth_type')

  // Clear irrelevant fields when auth type changes
  useEffect(() => {
    if (authType === 'none') {
      setValue('api_key', '')
      setValue('username', '')
      setValue('password', '')
    } else if (authType === 'token') {
      setValue('username', '')
      setValue('password', '')
    } else if (authType === 'basic') {
      setValue('api_key', '')
    }
  }, [authType, setValue])

  // Subscribe to all form value changes and notify parent
  useEffect(() => {
    const subscription = watch((values) => {
      onValuesChange?.(values as MCPFormData, isValid)
    })
    return () => subscription.unsubscribe()
  }, [watch, onValuesChange, isValid])

  return (
    <div className="space-y-4">
      {/* Server URL */}
      <div className="space-y-2">
        <Label className="text-gray-200 flex items-center gap-2">
          <Server className="h-4 w-4" />
          {t('config.serverUrl')}
          <span className="text-red-400">*</span>
        </Label>
        <Input
          {...register('server_url')}
          placeholder="https://mcp-server.example.com"
          className="bg-gray-800 border-gray-600 focus:border-cyan-500/50"
        />
        {errors.server_url && (
          <p className="text-sm text-red-400">{errors.server_url.message}</p>
        )}
      </div>

      {/* Auth Type */}
      <div className="space-y-2">
        <Label className="text-gray-200 flex items-center gap-2">
          <Lock className="h-4 w-4" />
          {t('config.authType')}
        </Label>
        <Controller
          name="auth_type"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onValueChange={(value: MCPAuthType) => {
                field.onChange(value)
              }}
            >
              <SelectTrigger className="bg-gray-800 border-gray-600 focus:border-cyan-500/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-600">
                <SelectItem value="none">{t('config.authNone')}</SelectItem>
                <SelectItem value="token">{t('config.authToken')}</SelectItem>
                <SelectItem value="basic">{t('config.authBasic')}</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* API Key - shown when auth_type is 'token' */}
      {authType === 'token' && (
        <div className="space-y-2">
          <Label className="text-gray-200 flex items-center gap-2">
            <Key className="h-4 w-4" />
            {t('config.apiKey')}
          </Label>
          <Input
            {...register('api_key')}
            type="password"
            placeholder="sk-..."
            className="bg-gray-800 border-gray-600 focus:border-cyan-500/50"
          />
          {errors.api_key && (
            <p className="text-sm text-red-400">{errors.api_key.message}</p>
          )}
          <p className="text-xs text-gray-500">{t('config.apiKeyHint')}</p>
        </div>
      )}

      {/* Username + Password - shown when auth_type is 'basic' */}
      {authType === 'basic' && (
        <>
          <div className="space-y-2">
            <Label className="text-gray-200 flex items-center gap-2">
              <User className="h-4 w-4" />
              {t('config.username')}
              <span className="text-red-400">*</span>
            </Label>
            <Input
              {...register('username')}
              placeholder="username"
              className="bg-gray-800 border-gray-600 focus:border-cyan-500/50"
            />
            {errors.username && (
              <p className="text-sm text-red-400">{errors.username.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-gray-200 flex items-center gap-2">
              <Key className="h-4 w-4" />
              {t('config.password')}
              <span className="text-red-400">*</span>
            </Label>
            <Input
              {...register('password')}
              type="password"
              placeholder="••••••••"
              className="bg-gray-800 border-gray-600 focus:border-cyan-500/50"
            />
            {errors.password && (
              <p className="text-sm text-red-400">{errors.password.message}</p>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export { type MCPFormData }
